/**
 * Modal for creating trackers.
 *
 * This modal follows the standard BaseModal pattern with customFooter,
 * similar to alert-form-modal and create-enrichment-modal.
 *
 * Supports three modes:
 * 1. Normalized: Type -> Search -> Sources -> Validation
 * 2. Import: Name -> Upload JSON
 * 3. Advanced: Source -> Tracker -> Configuration -> Frequency
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faHashtag,
  faUser,
  faUsers,
  faMapMarkerAlt,
  faNewspaper,
  faVideo,
  faImage,
  faBuilding,
  faFileAlt,
  faShoppingCart,
  faFileImport,
  faCog,
  faArrowLeft,
  faArrowRight,
  faCheck,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FaHashtag, FaSave } from "react-icons/fa";

import {
  useTrackers,
  useCreateTracker,
  useCreateTrackerImport,
  useImportTrackerDocuments,
} from "@/api/tracker";
import useNotif from "@/hooks/use-notif";
import useTranslation from "@/hooks/use-translation";
import { BaseModal } from "@/components/ui/base-modal";
import {
  Button,
  Card,
  CardBody,
  Chip,
  Typography,
  Input,
  Tabs,
  Textarea,
  Progress,
} from "@/components/ui";
import LuceneTextarea from "@/components/ui/lucene-textarea";
import { SourceIcon } from "@/components/resource-card";
import { useNormalizedSources, useNormalizedTrackersByType } from "@/hooks/use-normalized-trackers";
import { useApiSchema } from "@/utils/api-schema-provider";
import type {
  TrackerTypeName,
  NormalizedTrackerInfo,
  AllTrackerTypeName,
} from "@/models/normalized-tracker";
import {
  TRACKER_TYPES,
  SPECIAL_TRACKER_TYPES,
  TRACKER_TYPE_LABELS,
  TRACKER_TYPE_DESCRIPTIONS,
  TRACKER_TYPE_INPUT_LABELS,
  TRACKER_TYPE_INPUT_PLACEHOLDERS,
} from "@/models/normalized-tracker";
import type { TrackerSchemaModel } from "@/models/tracker";
import NormalizedTrackerForm, {
  type NormalizedTrackerFormValues,
  type NormalizedTrackerFormHandle,
} from "./NormalizedTrackerForm";
import TrackerForm from "./tracker-form";
import type { ExpandedTrackerInfo } from "@/models/normalized-tracker";

// ============================================================================
// Types and Constants
// ============================================================================

type ModalMode = "normalized" | "import" | "advanced";

// Step constants for normalized mode
const STEP_SELECT_TYPE = 0;
const STEP_INPUT_VALUE = 1;
const STEP_SELECT_SOURCES = 2;
const STEP_CONFIGURE_PARAMS = 3;

// Step constants for import mode
const IMPORT_STEP_NAME = 0;
const IMPORT_STEP_UPLOAD = 1;

// Step constants for advanced mode
const ADVANCED_STEP_SOURCE = 0;
const ADVANCED_STEP_TRACKER = 1;
const ADVANCED_STEP_CONFIG = 2;

const STEPS_NORMALIZED = [
  { key: "type", label: "Type" },
  { key: "search", label: "Recherche" },
  { key: "sources", label: "Sources" },
  { key: "validation", label: "Validation" },
];

const STEPS_IMPORT = [
  { key: "name", label: "Nom" },
  { key: "upload", label: "Import" },
];

const STEPS_ADVANCED = [
  { key: "source", label: "Source" },
  { key: "tracker", label: "Tracker" },
  { key: "config", label: "Configuration" },
];

// Icons for tracker types
const TRACKER_TYPE_ICONS: Record<AllTrackerTypeName, React.ReactNode> = {
  keywords: <FontAwesomeIcon icon={faSearch} className="text-2xl" />,
  hashtags: <FontAwesomeIcon icon={faHashtag} className="text-2xl" />,
  persons: <FontAwesomeIcon icon={faUser} className="text-2xl" />,
  groups: <FontAwesomeIcon icon={faUsers} className="text-2xl" />,
  locations: <FontAwesomeIcon icon={faMapMarkerAlt} className="text-2xl" />,
  products: <FontAwesomeIcon icon={faShoppingCart} className="text-2xl" />,
  news: <FontAwesomeIcon icon={faNewspaper} className="text-2xl" />,
  videos: <FontAwesomeIcon icon={faVideo} className="text-2xl" />,
  images: <FontAwesomeIcon icon={faImage} className="text-2xl" />,
  companies: <FontAwesomeIcon icon={faBuilding} className="text-2xl" />,
  articles: <FontAwesomeIcon icon={faFileAlt} className="text-2xl" />,
  import: <FontAwesomeIcon icon={faFileImport} className="text-2xl" />,
  advanced: <FontAwesomeIcon icon={faCog} className="text-2xl" />,
};

const DEFAULT_TRACKER_CRON = "0 */6 * * *";

// ============================================================================
// Exports
// ============================================================================

export interface TrackerCreationData {
  trackerType: TrackerTypeName;
  source: string;
  endpoint: string;
  searchValue: string;
  params: Record<string, unknown>;
  trackerInfo: NormalizedTrackerInfo;
}

interface TrackerModalProps {
  open: boolean;
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

const TrackerModal: React.FC<TrackerModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { addSuccess, addError } = useNotif();
  const { refetch: refreshTrackers } = useTrackers();
  const createTracker = useCreateTracker();
  const createTrackerImport = useCreateTrackerImport();
  const importTrackerDocuments = useImportTrackerDocuments();
  const { apiSchema } = useApiSchema();

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  // Mode and step
  const [mode, setMode] = useState<ModalMode>("normalized");
  const [currentStep, setCurrentStep] = useState(STEP_SELECT_TYPE);

  // Normalized mode state
  const [selectedType, setSelectedType] = useState<TrackerTypeName | null>(null);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  // Import mode state
  const [importName, setImportName] = useState("");
  const [createdTrackerId, setCreatedTrackerId] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<"file" | "text">("file");
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [jsonText, setJsonText] = useState("");
  const [jsonData, setJsonData] = useState<unknown[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Advanced mode state
  const [advancedSource, setAdvancedSource] = useState<string | null>(null);
  const [advancedTracker, setAdvancedTracker] = useState<TrackerSchemaModel | null>(null);

  // Common state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form refs
  const formRef = React.useRef<NormalizedTrackerFormHandle>(null);
  const advancedForm = useForm({ shouldUnregister: true });

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const { data: allSources, isLoading: isLoadingSources } = useNormalizedSources();

  const { data: trackerInfo, isLoading: isLoadingTrackerInfo } = useNormalizedTrackersByType(
    selectedType,
    selectedSources,
    selectedSources.length > 0 && !!searchValue,
    searchValue || undefined
  );

  // -------------------------------------------------------------------------
  // Computed values
  // -------------------------------------------------------------------------

  const sourcesForType = useMemo(() => {
    if (!allSources || !selectedType) return [];
    return allSources
      .filter((s) => s.types.includes(selectedType))
      .sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        return a.source.localeCompare(b.source);
      });
  }, [allSources, selectedType]);

  const sourceCountByType = useMemo(() => {
    if (!allSources) return {};
    const counts: Record<string, number> = {};
    for (const type of TRACKER_TYPES) {
      counts[type] = allSources.filter((s) => s.types.includes(type) && s.available).length;
    }
    return counts;
  }, [allSources]);

  const totalSourceCountByType = useMemo(() => {
    if (!allSources) return {};
    const counts: Record<string, number> = {};
    for (const type of TRACKER_TYPES) {
      counts[type] = allSources.filter((s) => s.types.includes(type)).length;
    }
    return counts;
  }, [allSources]);

  // Advanced mode: available sources from API schema
  const advancedSources = useMemo(() => {
    if (!apiSchema?.sources) return [];
    return Object.entries(apiSchema.sources)
      .filter(([, source]) => source.trackers && source.trackers.length > 0)
      .map(([key, source]) => ({
        key,
        name: source.metadata.name,
        description: source.metadata.description,
        logo: source.metadata.logo,
        trackerCount: source.trackers.length,
      }));
  }, [apiSchema]);

  // Advanced mode: trackers for selected source
  const advancedTrackers = useMemo(() => {
    if (!advancedSource || !apiSchema?.sources?.[advancedSource]) return [];
    return apiSchema.sources[advancedSource].trackers.map((t) => t.tracker);
  }, [advancedSource, apiSchema]);

  // Get current steps based on mode
  const currentSteps = useMemo(() => {
    switch (mode) {
      case "import":
        return STEPS_IMPORT;
      case "advanced":
        return STEPS_ADVANCED;
      default:
        return STEPS_NORMALIZED;
    }
  }, [mode]);

  // Check if current step is valid
  const isStepValid = useMemo(() => {
    if (mode === "normalized") {
      switch (currentStep) {
        case STEP_SELECT_TYPE:
          return selectedType !== null;
        case STEP_INPUT_VALUE:
          return searchValue.trim().length > 0;
        case STEP_SELECT_SOURCES:
          return selectedSources.length > 0;
        case STEP_CONFIGURE_PARAMS:
          return true;
        default:
          return false;
      }
    } else if (mode === "import") {
      switch (currentStep) {
        case IMPORT_STEP_NAME:
          return importName.trim().length > 0;
        case IMPORT_STEP_UPLOAD:
          return jsonData.length > 0;
        default:
          return false;
      }
    } else if (mode === "advanced") {
      switch (currentStep) {
        case ADVANCED_STEP_SOURCE:
          return advancedSource !== null;
        case ADVANCED_STEP_TRACKER:
          return advancedTracker !== null;
        case ADVANCED_STEP_CONFIG:
          return true;
        default:
          return false;
      }
    }
    return false;
  }, [mode, currentStep, selectedType, searchValue, selectedSources, importName, jsonData, advancedSource, advancedTracker]);

  const isLastStep = currentStep === currentSteps.length - 1;

  const lineCount = searchValue
    .split("\n")
    .filter((line) => line.trim().length > 0).length;

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (open) {
      resetAllState();
    }
  }, [open]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const resetAllState = () => {
    setMode("normalized");
    setCurrentStep(STEP_SELECT_TYPE);
    setSelectedType(null);
    setSelectedSources([]);
    setSearchValue("");
    setImportName("");
    setCreatedTrackerId(null);
    setUploadMode("file");
    setJsonFile(null);
    setJsonText("");
    setJsonData([]);
    setIsUploading(false);
    setAdvancedSource(null);
    setAdvancedTracker(null);
    setIsSubmitting(false);
    advancedForm.reset();
  };

  const handleSelectType = (type: AllTrackerTypeName) => {
    if (type === "import") {
      setMode("import");
      setCurrentStep(IMPORT_STEP_NAME);
      return;
    }
    if (type === "advanced") {
      setMode("advanced");
      setCurrentStep(ADVANCED_STEP_SOURCE);
      return;
    }

    setSelectedType(type as TrackerTypeName);
    setSearchValue("");
    setSelectedSources([]);
    setCurrentStep(STEP_INPUT_VALUE);
  };

  const handleToggleSource = (source: string) => {
    setSelectedSources((prev) =>
      prev.includes(source)
        ? prev.filter((s) => s !== source)
        : [...prev, source]
    );
  };

  const handleNext = () => {
    if (currentStep < currentSteps.length - 1 && isStepValid) {
      // Special case for import mode: create tracker before going to upload step
      if (mode === "import" && currentStep === IMPORT_STEP_NAME) {
        handleCreateImportTracker();
        return;
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      // Go back to type selection
      if (mode !== "normalized") {
        setMode("normalized");
        setCurrentStep(STEP_SELECT_TYPE);
        // Reset mode-specific state
        if (mode === "import") {
          setImportName("");
          setCreatedTrackerId(null);
          setJsonFile(null);
          setJsonText("");
          setJsonData([]);
        } else if (mode === "advanced") {
          setAdvancedSource(null);
          setAdvancedTracker(null);
          advancedForm.reset();
        }
      }
      return;
    }

    if (mode === "normalized" && currentStep === STEP_INPUT_VALUE) {
      setSelectedType(null);
      setSearchValue("");
    }

    setCurrentStep(currentStep - 1);
  };

  // -------------------------------------------------------------------------
  // Normalized mode handlers
  // -------------------------------------------------------------------------

  const handleFormSubmit = useCallback(async (values: NormalizedTrackerFormValues) => {
    if (!selectedType || !trackerInfo || !searchValue) return;

    setIsSubmitting(true);

    try {
      const trackersToCreate: TrackerCreationData[] = [];

      const filteredSources = trackerInfo.sources.filter(
        (entry: ExpandedTrackerInfo) => selectedSources.includes(entry.source)
      );

      for (const expanded of filteredSources) {
        const additionalParams = values.sources[expanded.source] || {};
        const searchParamName = expanded.info.configured_params
          ? Object.keys(expanded.info.configured_params)[0]
          : "query";

        trackersToCreate.push({
          trackerType: selectedType,
          source: expanded.source,
          endpoint: expanded.info.endpoint,
          searchValue: expanded.search_term,
          params: { [searchParamName]: expanded.search_term, ...additionalParams },
          trackerInfo: expanded.info,
        });
      }

      const sharedTag = trackersToCreate.length > 1
        ? `${TRACKER_TYPE_LABELS[selectedType]} - ${searchValue}`.trim()
        : undefined;

      await Promise.all(
        trackersToCreate.map((tracker) =>
          createTracker.mutateAsync({
            path: tracker.endpoint,
            name: tracker.searchValue,
            params: tracker.params,
            cron: DEFAULT_TRACKER_CRON,
            ...(sharedTag && { tags: [sharedTag] }),
          })
        )
      );

      addSuccess(
        trackersToCreate.length > 1
          ? `${trackersToCreate.length} trackers créés avec succès`
          : "Tracker créé avec succès"
      );

      refreshTrackers();
      onClose();
    } catch (error) {
      console.error("Error creating trackers:", error);
      addError("Erreur lors de la création des trackers");
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedType, trackerInfo, searchValue, selectedSources, createTracker, addSuccess, addError, refreshTrackers, onClose]);

  const handleCreate = () => {
    if (mode === "normalized" && formRef.current) {
      formRef.current.submit();
    } else if (mode === "import") {
      handleUploadDocuments();
    } else if (mode === "advanced") {
      advancedForm.handleSubmit(handleCreateAdvancedTracker)();
    }
  };

  // -------------------------------------------------------------------------
  // Import mode handlers
  // -------------------------------------------------------------------------

  const handleCreateImportTracker = async () => {
    if (!importName.trim()) {
      addError("Le nom du tracker est requis");
      return;
    }

    setIsSubmitting(true);

    try {
      const createdTracker = await createTrackerImport.mutateAsync({
        is_import: true,
        metadata: {
          name: importName.trim(),
          nb_docs: 0,
        },
        params: {},
      });

      addSuccess("Tracker d'import créé avec succès");
      setCreatedTrackerId(createdTracker.id);
      setCurrentStep(IMPORT_STEP_UPLOAD);
    } catch (error) {
      addError("Erreur lors de la création du tracker");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".json")) {
      addError("Veuillez sélectionner un fichier JSON");
      return;
    }

    setJsonFile(file);

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      if (!Array.isArray(parsed)) {
        addError("Le fichier JSON doit contenir un tableau de documents");
        setJsonFile(null);
        return;
      }

      setJsonData(parsed);
      addSuccess(t("n_documents_loaded", { count: parsed.length }));
    } catch {
      addError("Erreur lors de la lecture du fichier JSON");
      setJsonFile(null);
      setJsonData([]);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setJsonText(text);

    if (text.trim()) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          setJsonData(parsed);
        } else {
          setJsonData([]);
        }
      } catch {
        setJsonData([]);
      }
    } else {
      setJsonData([]);
    }
  };

  const handleParseText = () => {
    if (!jsonText.trim()) {
      addError("Veuillez saisir du JSON");
      return;
    }

    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        addError("Le JSON doit contenir un tableau de documents");
        setJsonData([]);
        return;
      }

      setJsonData(parsed);
      addSuccess(t("n_documents_loaded", { count: parsed.length }));
    } catch {
      addError("Erreur lors du parsing du JSON. Vérifiez la syntaxe.");
      setJsonData([]);
    }
  };

  const handleUploadDocuments = async () => {
    if (!createdTrackerId || jsonData.length === 0) {
      addError("Aucun document à importer");
      return;
    }

    setIsUploading(true);

    try {
      await importTrackerDocuments.mutateAsync({
        _: jsonData,
        uriParams: { trackerId: createdTrackerId },
      });

      addSuccess(t("n_documents_imported", { count: jsonData.length }));
      refreshTrackers();
      onClose();
    } catch {
      addError("Erreur lors de l'import des documents");
    } finally {
      setIsUploading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Advanced mode handlers
  // -------------------------------------------------------------------------

  const handleSelectAdvancedSource = (sourceKey: string) => {
    setAdvancedSource(sourceKey);
    setAdvancedTracker(null);
    setCurrentStep(ADVANCED_STEP_TRACKER);
  };

  const handleSelectAdvancedTracker = (tracker: TrackerSchemaModel) => {
    setAdvancedTracker(tracker);
    advancedForm.reset();
    setCurrentStep(ADVANCED_STEP_CONFIG);
  };

  const handleCreateAdvancedTracker = async (data: Record<string, unknown>) => {
    if (!advancedTracker) return;

    setIsSubmitting(true);

    try {
      // Clean params
      const params: Record<string, unknown> = {};
      const paramData = (data.parameter || {}) as Record<string, unknown>;
      for (const [key, value] of Object.entries(paramData)) {
        if (value !== "" && value !== undefined && value !== null) {
          if (Array.isArray(value) && value.length === 0) continue;
          params[key] = value;
        }
      }

      await createTracker.mutateAsync({
        path: advancedTracker.path,
        params,
        cron: DEFAULT_TRACKER_CRON,
      });

      addSuccess("Tracker créé avec succès");
      refreshTrackers();
      onClose();
    } catch {
      addError("Erreur lors de la création du tracker");
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const getModalTitle = () => {
    switch (mode) {
      case "import":
        return "Importer des données";
      case "advanced":
        return "Tracker avancé";
      default:
        return "Créer un Tracker";
    }
  };

  const getModalSubtitle = () => {
    switch (mode) {
      case "import":
        return currentStep === IMPORT_STEP_NAME
          ? "Donnez un nom à votre tracker d'import"
          : "Importez vos documents JSON";
      case "advanced":
        if (advancedSource && currentStep > ADVANCED_STEP_SOURCE) {
          const sourceName = apiSchema?.sources?.[advancedSource]?.metadata.name || advancedSource;
          return advancedTracker
            ? `${sourceName} • ${advancedTracker.name}`
            : sourceName;
        }
        return "Accédez à tous les trackers disponibles";
      default:
        if (selectedType && currentStep > STEP_SELECT_TYPE) {
          return `${TRACKER_TYPE_LABELS[selectedType]}${searchValue ? ` : ${searchValue}` : ""}`;
        }
        return "Sélectionnez un type de tracker et configurez-le";
    }
  };

  const getModalIcon = () => {
    switch (mode) {
      case "import":
        return <FontAwesomeIcon icon={faFileImport} />;
      case "advanced":
        return <FontAwesomeIcon icon={faCog} />;
      default:
        return <FaHashtag />;
    }
  };

  // -------------------------------------------------------------------------
  // Render: Type selection (step 0 for normalized mode)
  // -------------------------------------------------------------------------

  const renderSelectType = () => {
    const hasSourcesData = allSources && allSources.length > 0;

    return (
      <div className="flex flex-col gap-4">
        <Typography variant="h6" className="text-content-primary">
          Choisissez un type de tracker
        </Typography>

        {isLoadingSources && (
          <div className="p-3 bg-surface-secondary rounded-lg">
            <Typography variant="small" color="secondary">
              Chargement des sources disponibles...
            </Typography>
          </div>
        )}

        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
        >
          {/* Special types: Import and Advanced */}
          {SPECIAL_TRACKER_TYPES.map((type) => (
            <Card
              key={type}
              variant="outlined"
              hover
              clickable
              className="h-full flex flex-col hover:border-accent"
              onClick={() => handleSelectType(type)}
            >
              <CardBody className="p-4 flex flex-col items-center justify-center h-full text-center">
                <div className="text-accent mb-2">{TRACKER_TYPE_ICONS[type]}</div>
                <Typography variant="text-sm" className="font-semibold mb-1">
                  {TRACKER_TYPE_LABELS[type]}
                </Typography>
                <Typography variant="small" color="tertiary" className="mb-2">
                  {TRACKER_TYPE_DESCRIPTIONS[type]}
                </Typography>
              </CardBody>
            </Card>
          ))}

          {/* Normalized types */}
          {!isLoadingSources &&
            TRACKER_TYPES.map((type) => {
              const availableCount = sourceCountByType[type] || 0;
              const totalCount = totalSourceCountByType[type] || 0;
              if (!hasSourcesData || totalCount === 0) return null;

              const hasAvailableSources = availableCount > 0;

              return (
                <Card
                  key={type}
                  variant="outlined"
                  hover={hasAvailableSources}
                  clickable={hasAvailableSources}
                  className={`h-full flex flex-col ${
                    hasAvailableSources ? "hover:border-accent" : "opacity-60 cursor-not-allowed"
                  }`}
                  onClick={() => hasAvailableSources && handleSelectType(type)}
                >
                  <CardBody className="p-4 flex flex-col items-center justify-center h-full text-center">
                    <div className={`mb-2 ${hasAvailableSources ? "text-accent" : "text-content-tertiary"}`}>
                      {TRACKER_TYPE_ICONS[type]}
                    </div>
                    <Typography variant="text-sm" className="font-semibold mb-1">
                      {TRACKER_TYPE_LABELS[type]}
                    </Typography>
                    <Typography variant="small" color="tertiary" className="mb-2">
                      {TRACKER_TYPE_DESCRIPTIONS[type]}
                    </Typography>
                    <Chip size="sm" variant="soft" color={hasAvailableSources ? "primary" : "default"}>
                      {t("n_sources_available", { count: availableCount, total: totalCount })}
                    </Chip>
                  </CardBody>
                </Card>
              );
            })}
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Render: Normalized mode steps
  // -------------------------------------------------------------------------

  const renderInputValue = () => {
    if (!selectedType) return null;

    return (
      <div className="flex flex-col gap-4">
        <div>
          <Typography variant="h6" className="text-content-primary">
            {TRACKER_TYPE_INPUT_LABELS[selectedType]}
          </Typography>
          <Typography variant="body2" color="secondary">
            Type: {TRACKER_TYPE_LABELS[selectedType]}
          </Typography>
        </div>

        <Card variant="outlined" className="border-accent bg-accent-muted/30">
          <CardBody className="space-y-4">
            <div>
              <Typography variant="body2" color="secondary">
                Entrez vos requêtes de recherche. Chaque ligne sera traitée séparément.
              </Typography>
              <Typography variant="small" color="tertiary" className="mt-1">
                Syntaxe supportée : <code className="bg-surface-secondary px-1 rounded">AND</code>,{" "}
                <code className="bg-surface-secondary px-1 rounded">OR</code>,{" "}
                <code className="bg-surface-secondary px-1 rounded">NOT</code>,{" "}
                parenthèses <code className="bg-surface-secondary px-1 rounded">()</code>,{" "}
                guillemets <code className="bg-surface-secondary px-1 rounded">"phrase exacte"</code>
              </Typography>
            </div>
            <LuceneTextarea
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={`${TRACKER_TYPE_INPUT_PLACEHOLDERS[selectedType]}\nmacron AND politique\nbitcoin OR crypto`}
              minRows={4}
              maxRows={10}
            />
            {lineCount > 0 && (
              <Typography variant="small" color="tertiary">
                {t("n_queries_configured", { count: lineCount })}
              </Typography>
            )}
          </CardBody>
        </Card>
      </div>
    );
  };

  const renderSelectSources = () => {
    const availableCount = sourcesForType.filter((s) => s.available).length;

    return (
      <div className="flex flex-col gap-4">
        <div>
          <Typography variant="h6" className="text-content-primary">
            Choisissez une ou plusieurs sources
          </Typography>
          <Typography variant="body2" color="secondary">
            {selectedType && TRACKER_TYPE_LABELS[selectedType]} : <strong>{searchValue}</strong>
          </Typography>
        </div>

        {isLoadingSources ? (
          <div className="text-center py-8">
            <Typography variant="body1" color="secondary">
              Chargement des sources...
            </Typography>
          </div>
        ) : sourcesForType.length === 0 ? (
          <div className="p-4 bg-warning-light/20 border border-warning rounded-lg">
            <Typography variant="body2" color="secondary">
              Aucune source configurée pour ce type de tracker.
            </Typography>
          </div>
        ) : availableCount === 0 ? (
          <div className="p-4 bg-warning-light/20 border border-warning rounded-lg mb-2">
            <Typography variant="body2" color="secondary">
              Aucune source disponible.
            </Typography>
          </div>
        ) : null}

        {sourcesForType.length > 0 && (
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {sourcesForType.map(({ source, available }) => {
              const isSelected = selectedSources.includes(source);
              const isDisabled = !available;

              return (
                <Card
                  key={source}
                  variant="outlined"
                  hover={!isDisabled}
                  clickable={!isDisabled}
                  className={`relative transition-all ${
                    isDisabled
                      ? "opacity-50 cursor-not-allowed bg-surface-secondary"
                      : isSelected
                        ? "border-2 border-accent bg-accent-muted"
                        : "hover:border-accent"
                  }`}
                  onClick={() => !isDisabled && handleToggleSource(source)}
                >
                  <CardBody className="p-4 flex flex-col items-center justify-center text-center gap-2">
                    {!isDisabled && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSource(source)}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-2 right-2 w-4 h-4 cursor-pointer accent-accent"
                      />
                    )}
                    <SourceIcon source={source} size="lg" />
                    <Typography
                      variant="text-sm"
                      className={`font-semibold capitalize ${isDisabled ? "text-content-tertiary" : ""}`}
                    >
                      {source}
                    </Typography>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderConfigureParams = () => {
    if (!selectedType) return null;

    if (isLoadingTrackerInfo || !trackerInfo) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Typography variant="body1" color="secondary">
            Chargement des paramètres...
          </Typography>
        </div>
      );
    }

    return (
      <NormalizedTrackerForm
        ref={formRef}
        trackerType={selectedType}
        selectedSources={selectedSources}
        searchValue={searchValue}
        trackerInfo={trackerInfo}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        submitLabel="Créer les trackers"
        hideButtons
      />
    );
  };

  // -------------------------------------------------------------------------
  // Render: Import mode steps
  // -------------------------------------------------------------------------

  const renderImportName = () => (
    <div className="flex flex-col gap-4">
      <Typography variant="h6" className="text-content-primary">
        Créer un tracker d'import
      </Typography>
      <Typography variant="body2" color="secondary">
        Donnez un nom à votre tracker d'import. Vous pourrez ensuite importer vos données.
      </Typography>

      <Input
        value={importName}
        onChange={(e) => setImportName(e.target.value)}
        label="Nom du tracker"
        placeholder="Ex: Import Twitter Janvier 2024"
        required
      />
    </div>
  );

  const renderImportUpload = () => (
    <div className="flex flex-col gap-4">
      <Typography variant="h6" className="text-content-primary">
        Importer vos documents
      </Typography>
      <Typography variant="body2" color="secondary">
        Choisissez d'importer un fichier JSON ou collez directement le JSON contenant un tableau de documents.
      </Typography>

      <Tabs
        value={uploadMode}
        onChange={(newValue) => {
          setUploadMode(newValue as "file" | "text");
          setJsonData([]);
          setJsonFile(null);
          setJsonText("");
        }}
      >
        <Tabs.List variant="underline">
          <Tabs.Tab value="file">Fichier</Tabs.Tab>
          <Tabs.Tab value="text">Texte</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {uploadMode === "file" ? (
        <div>
          <input
            accept=".json"
            className="hidden"
            id="json-file-input"
            type="file"
            onChange={handleFileSelect}
          />
          <Button
            variant="outline"
            leftIcon={<FontAwesomeIcon icon={faUpload} />}
            onClick={() => document.getElementById("json-file-input")?.click()}
          >
            Sélectionner un fichier JSON
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Textarea
            value={jsonText}
            onChange={handleTextChange}
            placeholder='[{"id": 1, "content": "..."}, {"id": 2, "content": "..."}]'
            rows={8}
            className="font-mono text-sm"
          />
          <Button variant="outline" onClick={handleParseText}>
            Valider le JSON
          </Button>
        </div>
      )}

      {(jsonFile || (uploadMode === "text" && jsonData.length > 0)) && (
        <div className="p-4 bg-surface-secondary rounded-lg border border-arc-border">
          {uploadMode === "file" && jsonFile && (
            <Typography variant="text-sm" className="font-medium mb-2">
              Fichier sélectionné : {jsonFile.name}
            </Typography>
          )}
          <Typography variant="text-sm" color="secondary">
            {t("n_documents_ready", { count: jsonData.length })}
          </Typography>
        </div>
      )}

      {isUploading && (
        <div>
          <Progress value={50} size="md" color="primary" />
          <Typography variant="text-sm" color="secondary" className="mt-2 text-center">
            Import en cours...
          </Typography>
        </div>
      )}
    </div>
  );

  // -------------------------------------------------------------------------
  // Render: Advanced mode steps
  // -------------------------------------------------------------------------

  const renderAdvancedSourceSelection = () => (
    <div className="flex flex-col gap-4">
      <Typography variant="h6" className="text-content-primary">
        Choisissez une source
      </Typography>

      {advancedSources.length === 0 ? (
        <div className="p-4 bg-warning-light/20 border border-warning rounded-lg">
          <Typography variant="body2" color="secondary">
            Aucune source disponible.
          </Typography>
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {advancedSources.map((source) => (
            <Card
              key={source.key}
              variant="outlined"
              hover
              clickable
              className="h-full flex flex-col hover:border-accent"
              onClick={() => handleSelectAdvancedSource(source.key)}
            >
              <CardBody className="p-4 flex flex-col items-center justify-center h-full text-center">
                <SourceIcon source={source.key} size="lg" />
                <Typography variant="text-sm" className="font-semibold capitalize mt-2 mb-1">
                  {source.name}
                </Typography>
                <Chip size="sm" variant="soft" color="primary">
                  {t("n_trackers_count", { count: source.trackerCount })}
                </Chip>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderAdvancedTrackerSelection = () => (
    <div className="flex flex-col gap-4">
      <Typography variant="h6" className="text-content-primary">
        Choisissez un tracker
      </Typography>
      <Typography variant="body2" color="secondary">
        Source: {apiSchema?.sources?.[advancedSource!]?.metadata.name || advancedSource}
      </Typography>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
        {advancedTrackers.map((tracker) => (
          <Card
            key={tracker.path}
            variant="outlined"
            hover
            clickable
            className="h-full flex flex-col hover:border-accent"
            onClick={() => handleSelectAdvancedTracker(tracker)}
          >
            <CardBody className="p-4">
              <Typography variant="text-sm" className="font-semibold mb-1">
                {tracker.name}
              </Typography>
              {tracker.description && (
                <Typography variant="small" color="tertiary">
                  {tracker.description}
                </Typography>
              )}
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderAdvancedConfig = () => {
    if (!advancedTracker) return null;

    return (
      <div className="flex flex-col gap-4">
        <div>
          <Typography variant="h6" className="text-content-primary">
            Configuration du tracker
          </Typography>
          <Typography variant="body2" color="secondary">
            {advancedTracker.name}
          </Typography>
        </div>

        <Card variant="outlined">
          <CardBody>
            <TrackerForm
              tracker={advancedTracker}
              control={advancedForm.control}
              setValue={advancedForm.setValue}
            />
          </CardBody>
        </Card>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Render: Step content
  // -------------------------------------------------------------------------

  const renderStepContent = () => {
    if (mode === "normalized") {
      switch (currentStep) {
        case STEP_SELECT_TYPE:
          return renderSelectType();
        case STEP_INPUT_VALUE:
          return renderInputValue();
        case STEP_SELECT_SOURCES:
          return renderSelectSources();
        case STEP_CONFIGURE_PARAMS:
          return renderConfigureParams();
        default:
          return null;
      }
    } else if (mode === "import") {
      switch (currentStep) {
        case IMPORT_STEP_NAME:
          return renderImportName();
        case IMPORT_STEP_UPLOAD:
          return renderImportUpload();
        default:
          return null;
      }
    } else if (mode === "advanced") {
      switch (currentStep) {
        case ADVANCED_STEP_SOURCE:
          return renderAdvancedSourceSelection();
        case ADVANCED_STEP_TRACKER:
          return renderAdvancedTrackerSelection();
        case ADVANCED_STEP_CONFIG:
          return renderAdvancedConfig();
        default:
          return null;
      }
    }
    return null;
  };

  // -------------------------------------------------------------------------
  // Render: Footer
  // -------------------------------------------------------------------------

  const renderFooter = () => (
    <div className="flex items-center justify-between w-full">
      {/* Step indicators */}
      <div className="flex items-center gap-2">
        {currentSteps.map((step, index) => (
          <div
            key={step.key}
            className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
              ${
                index === currentStep
                  ? "bg-accent text-white"
                  : index < currentStep
                    ? "bg-emerald-500 text-white"
                    : "bg-surface-secondary text-content-muted"
              }
            `}
          >
            {index < currentStep ? (
              <FontAwesomeIcon icon={faCheck} className="text-xs" />
            ) : (
              index + 1
            )}
          </div>
        ))}
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-2">
        {(currentStep > 0 || mode !== "normalized") && (
          <Button
            variant="ghost"
            onClick={handleBack}
            leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
          >
            Retour
          </Button>
        )}
        {!isLastStep ? (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!isStepValid || isSubmitting}
            loading={isSubmitting}
            rightIcon={<FontAwesomeIcon icon={faArrowRight} />}
          >
            Suivant
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!isStepValid || isSubmitting || isUploading}
            loading={isSubmitting || isUploading}
            leftIcon={<FaSave />}
          >
            {mode === "import" ? "Importer" : "Créer"}
          </Button>
        )}
      </div>
    </div>
  );

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      icon={getModalIcon()}
      title={getModalTitle()}
      subtitle={getModalSubtitle()}
      variant="accent"
      size="lg"
      className="!max-w-[1000px]"
      customFooter={renderFooter()}
    >
      <div className="min-h-[400px]">{renderStepContent()}</div>
    </BaseModal>
  );
};

export default TrackerModal;
